import React, { useState } from "react";
import { getAsrDocumentDetail } from "../../services/api";

const inputCls = "border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary";

const AsrDocumentDetail: React.FC = () => {
  const [ticketingProvider, setTicketingProvider] = useState("");
  const [documentNumber, setDocumentNumber]       = useState("");

  const [loading, setLoading]       = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [result, setResult]         = useState<any | null>(null);

  const canFetch = ticketingProvider.trim() && documentNumber.trim().length === 13;

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    setFetchError(null);
    setResult(null);
    try {
      const data = await getAsrDocumentDetail({
        ticketingProvider: ticketingProvider.trim().toUpperCase(),
        documentNumber: documentNumber.trim(),
      });
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch document detail."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">ASR: Document Detail</h1>
        <p className="text-sm text-gray-500 mt-1">
          Full detail of a single document via Sabre TKT_AsrServicesRQ DetailRQ.
          Requires SabreSonic airline access.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ticketing Provider *</label>
            <input value={ticketingProvider} onChange={(e) => setTicketingProvider(e.target.value.toUpperCase())}
              placeholder="e.g. 7F" maxLength={2} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Document Number * <span className="font-normal normal-case text-gray-400">(13 digits)</span></label>
            <input
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleFetch()}
              placeholder="2452104442771"
              maxLength={13}
              className={`${inputCls} font-mono`}
            />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={handleFetch} disabled={loading || !canFetch}
            className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50">
            {loading ? "Loading…" : "Fetch Detail"}
          </button>
          {documentNumber.length > 0 && documentNumber.length !== 13 && (
            <span className="ml-3 text-xs text-orange-500">Document number must be exactly 13 digits ({documentNumber.length}/13)</span>
          )}
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">{fetchError}</div>
      )}

      {result && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex flex-wrap gap-6 mb-4 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Document Number</span>
              <p className="font-bold font-mono text-heading text-base">{result.documentNumber}</p>
            </div>
          </div>
          <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-2">Raw Response</h3>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-700 overflow-auto max-h-[600px] whitespace-pre-wrap break-all">
            {JSON.stringify(result.rawData ?? result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AsrDocumentDetail;
